import { useCheckoutSession } from "@circle-react/components/Paywalls/Checkout/contexts/CheckoutSessionContext";
import { processWithRetries } from "@circle-react/components/Paywalls/Checkout/hooks/paymentMethods/helpers";
import { firePurchaseForCompletedSteps } from "@circle-react/components/Paywalls/CheckoutWithSession/helpers/ga4PurchaseHelper";
import { useCallback, useEffect, useRef } from "react";

import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";

const CANCELLED_STEP_COMPLETION_WAIT = { cancelled: true } as const;

interface UseWaitForUpgradeStepCompletionProps {
  goToNextStep: (session: CheckoutSession | null, stepId?: number) => boolean;
  refetchSession: () => Promise<CheckoutSession>;
  stepId: number;
}

export const useWaitForUpgradeStepCompletion = ({
  goToNextStep,
  refetchSession,
  stepId,
}: UseWaitForUpgradeStepCompletionProps) => {
  const isMountedRef = useRef(true);
  const { ga4FiredStepIdsRef } = useCheckoutSession();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const waitForStepCompletion = useCallback(async () => {
    const updatedSession = await processWithRetries<
      CheckoutSession | typeof CANCELLED_STEP_COMPLETION_WAIT
    >({
      executor: async () => {
        if (!isMountedRef.current) return CANCELLED_STEP_COMPLETION_WAIT;
        try {
          const freshSession = await refetchSession();
          if (!isMountedRef.current) return CANCELLED_STEP_COMPLETION_WAIT;
          return freshSession.steps.find(step => step.id === stepId)?.status ===
            "completed"
            ? freshSession
            : null;
        } catch {
          return isMountedRef.current ? null : CANCELLED_STEP_COMPLETION_WAIT;
        }
      },
    });
    if (
      !updatedSession ||
      "cancelled" in updatedSession ||
      !isMountedRef.current
    ) {
      return false;
    }

    firePurchaseForCompletedSteps(updatedSession, ga4FiredStepIdsRef);
    goToNextStep(updatedSession, stepId);
    return true;
  }, [ga4FiredStepIdsRef, goToNextStep, refetchSession, stepId]);

  return { isMountedRef, waitForStepCompletion };
};
